import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to detect user inactivity and trigger a callback after a specified timeout
 * @param {Function} onInactive - Callback function to execute when user becomes inactive
 * @param {number} timeout - Inactivity timeout in milliseconds (default: 15000ms = 15 seconds)
 * @param {Array} events - Array of events to listen for (default: common user interaction events)
 */
const useInactivityTimer = (
    onInactive,
    timeout = 15000, // 15 seconds for testing, change to hours in production
    events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
) => {
    const timeoutId = useRef(null);
    const hasTriggered = useRef(false);

    const resetTimer = useCallback(() => {
        // Clear existing timer
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }

        // Reset triggered flag
        hasTriggered.current = false;

        // Set new timer
        timeoutId.current = setTimeout(() => {
            if (!hasTriggered.current) {
                hasTriggered.current = true;
                onInactive();
            }
        }, timeout);
    }, [onInactive, timeout]);

    useEffect(() => {
        // Set initial timer
        resetTimer();

        // Add event listeners for all specified events
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        // Cleanup function
        return () => {
            // Remove all event listeners
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
            // Clear timeout
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [resetTimer, events]);

    return resetTimer; // Return resetTimer in case manual reset is needed
};

export default useInactivityTimer;
